import { useEffect, useRef, useState } from 'react';
import { Aviso } from '@/components/ui/aviso';
import { Etiqueta } from '@/components/ui/etiqueta';
import { Skeleton } from '@/components/ui/skeleton';
import { formatoFecha } from '@/lib/utils/formato-precio';
import { formatoPrecio, pisoEnTexto, techoEnTexto } from './barra-de-rangos';
import { PorQueEstePrecio } from './por-que-este-precio';
import { pricingRulesApi } from '../services/pricing-rules-api';
import type { FilaSimulada, ReglaRotaAbierta } from '../types/pricing-rules.types';
import { desdeFilaSimulada } from '../lib/recorrido-del-precio';
import { ETIQUETA_DE_TIEMPO, TITULOS } from '../lib/lectura-del-precio';

/**
 * «Por qué este precio, hoy» en el historial del producto y en Precios
 * Dinámicos. Desde el spec 225 el cuerpo es la explicación única
 * (`PorQueEstePrecio`); acá queda lo propio: las reglas rotas abiertas
 * (4B-datos T11: un precio puesto a mano que rompe una regla queda marcado) y el
 * aviso de que el precio de hoy no es el de su estrategia. Se piden a la vez;
 * si falla una, se muestra la otra. Nunca una sección vacía.
 */

const fmt = (n: number | null | undefined) => (n == null ? '—' : formatoPrecio(n));
const fecha = (iso: string) => formatoFecha(iso);

function rango(min: number | null, max: number | null): string {
  if (min != null && max != null) return `entre ${pisoEnTexto(min)} y ${techoEnTexto(max)}`;
  if (min != null) return `desde ${pisoEnTexto(min)}`;
  if (max != null) return `hasta ${techoEnTexto(max)}`;
  return 'cualquier precio';
}

/**
 * `avisaSiNoEsElDeSuEstrategia` (plan 3, T3; 206 §9 decisión 2): Precios Dinámicos
 * dice cuando el precio de hoy (puesto a mano o por archivo) no es el que calcula
 * su estrategia. La misma regla que `revisarAntesDeGuardar` (4B T8): hay
 * estrategia que calcula y su propuesta no es el precio de hoy. El historial no
 * la pasa: ahí cada entrada ya cuenta de dónde salió su precio.
 */
export function ReglasHoyDelProducto({
  productId,
  avisaSiNoEsElDeSuEstrategia = false,
  onFila,
}: {
  productId: string;
  avisaSiNoEsElDeSuEstrategia?: boolean;
  /** La fila que calculó `explicar` (o null si falló): quien la monta decide con ella. */
  onFila?: (f: FilaSimulada | null) => void;
}) {
  const [fila, setFila] = useState<FilaSimulada | null>(null);
  const [rotas, setRotas] = useState<ReglaRotaAbierta[]>([]);
  const [estado, setEstado] = useState<'CARGANDO' | 'LISTO' | 'ERROR'>('CARGANDO');
  // Si fallan las reglas rotas se dice: una lista que desaparece se lee como «no hay».
  const [rotasFallaron, setRotasFallaron] = useState(false);
  // El efecto se dispara por productId solamente; el callback se lee en su versión
  // más nueva sin agregarlo a las dependencias (ronda 2 de la revisión, E2).
  const onFilaRef = useRef(onFila);
  onFilaRef.current = onFila;

  useEffect(() => {
    let vivo = true;
    setEstado('CARGANDO');
    setRotasFallaron(false);
    Promise.allSettled([
      pricingRulesApi.explicar({ productId }),
      pricingRulesApi.reglasRotas(productId),
    ]).then(([e, r]) => {
      if (!vivo) return;
      setFila(e.status === 'fulfilled' ? e.value : null);
      onFilaRef.current?.(e.status === 'fulfilled' ? e.value : null);
      setRotas(r.status === 'fulfilled' ? r.value.abiertas : []);
      setRotasFallaron(r.status === 'rejected');
      setEstado(e.status === 'fulfilled' ? 'LISTO' : 'ERROR');
    });
    return () => {
      vivo = false;
    };
  }, [productId]);

  const propio = (
    <>
      {rotas.length > 0 && (
        <Aviso tono="alerta">
          <ul className="space-y-1">
            {rotas.map((r) => (
              <li key={r.id}>
                Precio puesto{' '}
                {r.origen === 'CSV' ? 'por archivo' : r.origen === 'API' ? 'por la API' : 'a mano'}{' '}
                el {fecha(r.createdAt)}
                {r.createdByName ? ` por ${r.createdByName}` : ''}: {fmt(r.precio)} no cumple «
                {r.ruleName}», que deja {rango(r.min, r.max)}.
              </li>
            ))}
          </ul>
        </Aviso>
      )}
      {rotasFallaron && (
        <p className="text-xs text-alerta">
          No se pudo ver si hay precios puestos que no cumplen una regla.
        </p>
      )}
      {estado === 'LISTO' &&
        fila &&
        avisaSiNoEsElDeSuEstrategia &&
        fila.estrategia &&
        fila.origenDelPedido === 'ESTRATEGIA' &&
        fila.estado !== 'SIN_DATOS' &&
        fila.estado !== 'SIN_ESTRATEGIA' &&
        fila.estado !== 'SIN_PRECIO_POSIBLE' &&
        Math.abs(fila.precioPropuesto - fila.precioHoy) >= 0.005 && (
          <Aviso tono="alerta" data-testid="conflicto">
            Su estrategia «{fila.estrategia.name}» hoy lo dejaría en {fmt(fila.precioPropuesto)}: en
            su próxima corrida lo propone (o, si aplica sola, lo pone).
          </Aviso>
        )}
    </>
  );

  if (estado === 'LISTO' && fila)
    return <PorQueEstePrecio recorrido={desdeFilaSimulada(fila)} tiempo="HOY" antes={propio} />;

  return (
    <section
      aria-label={`${TITULOS.HOY}, ${ETIQUETA_DE_TIEMPO.HOY}`}
      className="space-y-3 rounded-lg border p-3"
    >
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold">{TITULOS.HOY}</h3>
        <Etiqueta>{ETIQUETA_DE_TIEMPO.HOY}</Etiqueta>
      </div>
      {propio}
      {estado === 'CARGANDO' && (
        <div className="space-y-2" aria-busy="true">
          <Skeleton className="h-3 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
          <span className="sr-only">Calculando…</span>
        </div>
      )}
      {estado === 'ERROR' && (
        <p className="text-xs text-muted-foreground">
          No se pudo calcular cómo lo dejan las reglas hoy.
        </p>
      )}
    </section>
  );
}
